import { useEffect, useState } from "react";
import axios from "axios";
import { getTrustpilotData } from "./helper";
import { KitCmsReview } from "@Pages/EssentialKit/components/KitReviews/KitReviews";
import styles from "./TrustPilotBanner.module.scss";

import Carousel from "@Components/Carousel";
import TPCarouselItem from "./TPCarouselItem";
import Loading from "@Components/Loading";
import { PAGE } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import HorizontalScroll from "@Components/HorizontalScroll";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import TrustPilotDynamicWidget from "@Components/TrustPilotDynamicWidget";
import config from "@Config/index";
import { WEB_VIEW_APP_EVENTS } from "@Utils/webApputils/analyticEvents";
import { sendWebViewEvent } from "@Utils/webviewEvents";
import useIsApp from "hooks/useIsApp";
import { AppState } from "@Store/index";
import { shallowEqual, useSelector } from "react-redux";

const TrustPilotReviewsBanner = ({
  uniCity = null,
  isDesktop = false,
  isHomePage = false,
  isSearchPage = false,
  fromBaidu = false,
  staticReviews,
  hideFlagBadge = false,
}: {
  uniCity: any;
  isDesktop?: boolean;
  isHomePage?: boolean;
  isSearchPage?: boolean;
  fromBaidu?: boolean;
  staticReviews?: KitCmsReview[];
  hideFlagBadge?: boolean;
}) => {
  // When static reviews are supplied, adapt KitCmsReview → tp shape and skip
  // all API / SSR logic entirely.
  const adaptedStaticReviews = staticReviews?.map((r) => ({
    edited_review: r.reviewText ?? "",
    user_name: r.reviewerName ?? "",
    university: r.reviewerUniversity ?? "",
    nationality: "",
  }));

  // Read SSR reviews directly from Redux store
  const ssrReviews = useSelector(
    ({ searchPageData }: AppState) => searchPageData.trustpilotReviews,
    shallowEqual,
  );

  // Initialize state with SSR data if available — this makes the first render on server
  // include the reviews content without needing any useEffect
  const hasSSRData = Boolean(ssrReviews && ssrReviews.length > 0 && isSearchPage && !isHomePage);
  const [reviewsArray, setReviewsArray] = useState<{ data: any[]; fetched: boolean }>({
    data: adaptedStaticReviews ?? (hasSSRData ? (ssrReviews as any[]) : []),
    fetched: Boolean(adaptedStaticReviews) || hasSSRData,
  });
  const { t } = useTranslationHook();
  const isApp = useIsApp();

  const { country } = useSelector(
    ({ appData }: AppState) => ({ country: appData.country }),
    shallowEqual,
  );

  const getData = async (cancelToken?: any) => {
    setReviewsArray((prev) => ({ ...prev, fetched: false }));

    let university: any = null;
    let destination_city: any = null;

    if (uniCity?.region?.region_type == "locality") {
      destination_city = uniCity.region.name;
    } else if (uniCity?.region?.region_type == "establishment") {
      destination_city = uniCity.region.parent_name;
      university = uniCity.region.name;
    }

    const countryToNationality: Record<string, string> = {
      IN: "Indian",
      UK: "British",
      GB: "British",
      EN: "British",
      SG: "Singaporean",
      NP: "Nepalese",
      IE: "Irish",
      BD: "Bangladeshi",
      DE: "German",
      PK: "Pakistani",
      ES: "Spanish",
      AU: "Australian",
      CA: "Canadian",
      IT: "Italian",
      FR: "French",
      AE: "Emirati",
    };
    const fallbackNationality = isHomePage ? "mixreviews" : "British";
    let nationality = countryToNationality[country] || fallbackNationality;
    if (config.IS_CHINA_APP) nationality = "China";
    const data = await getTrustpilotData(destination_city, nationality, university, cancelToken);
    if (data?.error && (axios.isCancel(data.error) || data.error?.message === "canceled")) {
      return;
    }
    setReviewsArray({ data, fetched: true });
  };

  useEffect(() => {
    // Skip fetch when static reviews are supplied or we already have SSR data
    if (adaptedStaticReviews || hasSSRData) return undefined;
    const source = axios.CancelToken.source();
    getData(source.token);
    return () => {
      source.cancel("Request canceled by cleanup");
    };
  }, [country]);

  const [activePos, setActivePos] = useState(1);
  const handleTrustpilotSlideChange = (pos: number) => {
    setActivePos(pos);
    gtagEventEmitter({ action: "trustpilot_review_scrolled", category: PAGE.SEARCH });
  };
  const mobileTitle = t("home.trustpilotreviewsbanner.trustpilotreviewsbannershorttitle");

  const ReviewsWithLoader = ({ children }: any) => (reviewsArray.fetched ? children : <Loading />);

  return (
    <>
      {!isDesktop && isSearchPage && (
        <div
          className={formatClassNames(
            styles.bottomBorder,
            isSearchPage && styles.bottomBorderWithMrgin,
          )}
        />
      )}
      <div
        className={formatClassNames(
          !isHomePage && styles.trustPilotBanner,
          !isHomePage && isDesktop ? styles.trustPilotBannerDesktop : "",
          isHomePage && styles.trustPilotBannerHome,
          isDesktop && isHomePage && styles.trustpilotHomeDesktopSection,
          isSearchPage && !isDesktop && styles.trustPilotBannerSearch,
        )}
        data-testid="homepage-trustpilot-section"
        id="trustpilotReviews"
      >
        <div className={styles.trustPilotContainer} style={{ marginBottom: 40 }}>
          {isDesktop && !isHomePage && (
            <div className={styles.trustPilotTop}>
              {!isHomePage && (
                <div className={styles.trustPilotSubdiv}>
                  {t("home.trustpilotreviewsbanner.trustpilotreviewsbannertitle")}
                </div>
              )}
              <TrustPilotDynamicWidget
                width="180px"
                margin="0 -17px 0 -16px"
                height="24px"
                templateId="5419b757fa0340045cd0c938"
              />
            </div>
          )}
          {isDesktop ? (
            <>
              {isHomePage && (
                <>
                  <div className={styles.tpHomeTopDp}>
                    <div>
                      <h3 className={styles.tpHomeTitle}>
                        {t("home.trustpilotreviewsbanner.trustpilotreviewsbannertitle")}
                      </h3>
                    </div>
                    <div
                      className={formatClassNames(
                        styles.readAllReviewsText,
                        config.IS_CHINA_APP && styles.customWidgetOffset,
                      )}
                      role="none"
                      onClick={() => {
                        gtagEventEmitter({
                          action: "clicked_read_all_reviews",
                          event_category: PAGE.HOME,
                        });
                      }}
                    >
                      {!fromBaidu && (
                        <TrustPilotDynamicWidget
                          width="180px"
                          height="24px"
                          templateId="5419b757fa0340045cd0c938"
                          chinaVariant="pillWithLogo"
                        />
                      )}
                    </div>
                  </div>
                </>
              )}
              <div
                className={formatClassNames(
                  styles.trustpilotDesktopContainer,
                  isSearchPage && styles.leftRightPadding,
                )}
              >
                {isHomePage ? (
                  <div className={styles.homepageHorizontalScrollTP}>
                    {reviewsArray?.data.length > 0 ? (
                      <>
                        <HorizontalScroll
                          bgColor="green"
                          showControls
                          className={styles.horizontalScroll}
                        >
                          {reviewsArray.data.map((tp, i) => (
                            <TPCarouselItem
                              isDesktop
                              isHomePage={isHomePage}
                              key={i}
                              tp={tp}
                              hideFlagBadge={hideFlagBadge}
                            />
                          ))}
                        </HorizontalScroll>
                      </>
                    ) : (
                      <div key={0}>No reviews to show</div>
                    )}
                  </div>
                ) : (
                  <ReviewsWithLoader>
                    {reviewsArray?.data.length > 0 ? (
                      reviewsArray.data.map((tp, i) => (
                        <TPCarouselItem isDesktop key={i} tp={tp} hideFlagBadge={hideFlagBadge} />
                      ))
                    ) : (
                      <div key={0}>{t("home.trustpilotreviewsbanner.noreviewtoshow")}</div>
                    )}
                  </ReviewsWithLoader>
                )}
              </div>
            </>
          ) : (
            <>
              {isHomePage && (
                <>
                  <div className={styles.tpHomeTop}>
                    <h3 className={styles.tpHomeTitle}>{mobileTitle}</h3>
                    <div
                      className={formatClassNames(
                        isApp && styles.trustpilotWidget,
                        styles.readAllReviewsText,
                        config.IS_CHINA_APP && styles.customWidgetOffset,
                      )}
                      role="none"
                      onClick={() => {
                        gtagEventEmitter({
                          action: "clicked_read_all_reviews",
                          event_category: PAGE.HOME,
                        });
                        sendWebViewEvent(WEB_VIEW_APP_EVENTS.OPEN_EXTERNAL_URL, {
                          url: "https://uk.trustpilot.com/review/amberstudent.com",
                        });
                      }}
                    >
                      {!fromBaidu && (
                        <TrustPilotDynamicWidget
                          height="24px"
                          width="140px"
                          templateId="5419b757fa0340045cd0c938"
                          margin="0 -4px -4px 0px"
                          chinaVariant="pill"
                        />
                      )}
                    </div>
                  </div>
                </>
              )}
              {!isHomePage && (
                <div className={styles.trustPilotTop}>
                  <TrustPilotDynamicWidget
                    width="100px"
                    height="40px"
                    margin="0px 0px 0px 0px"
                    templateId="5419b6ffb0d04a076446a9af"
                  />
                  {!isHomePage && (
                    <div className={styles.trustPilotSubdiv}>Trust of 50,000+ Students</div>
                  )}
                </div>
              )}
              <div className={styles.tpCarouselContainer}>
                {reviewsArray.fetched ? (
                  <Carousel
                    slideList={
                      reviewsArray?.data.length > 0 ? (
                        reviewsArray.data.map((tp, i) => (
                          <TPCarouselItem
                            isHomePage={isHomePage}
                            key={i}
                            tp={tp}
                            hideFlagBadge={hideFlagBadge}
                          />
                        ))
                      ) : (
                        <div key={0}>home.trustpilotreviewsbanner.noreviewtoshow</div>
                      )
                    }
                    activePos={activePos}
                    handleChangeSlide={handleTrustpilotSlideChange}
                    className={styles.carouselItemContainer}
                    sliderContainerClassName={styles.tpCarouselSlider}
                    showPartialSlidesOnEdge={isHomePage && !isDesktop}
                  />
                ) : (
                  <Loading />
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {isHomePage && (
        <div
          className={formatClassNames(
            styles.bottomBorder,
            isSearchPage && styles.bottomBorderWithMrgin,
            isSearchPage && styles.bottomMargin,
            isDesktop && styles.bottomBorderDesktop,
          )}
        />
      )}
    </>
  );
};

export default wrapperHOC(TrustPilotReviewsBanner, {
  componentName: "TrustPilotReviewsBanner-SearchListDesktop-SearchPage",
  showForChina: true,
  showForMiniProgram: false,
});
